import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Autoplay, Pagination, Navigation } from 'swiper/modules';
import { FaUsers, FaGamepad, FaTicketAlt, FaGift, FaShoppingCart } from 'react-icons/fa';
import CustomIcon from '../components/CustomIcon';
import HeroCarousel from '../components/HeroCarousel';
import SponsorLogoMarquee from '../components/SponsorLogoMarquee';
import MoonLogo from '../components/MoonLogo';

import 'swiper/css';
import 'swiper/css/pagination';
import 'swiper/css/navigation';

// Coin düşme animasyonu için CSS
const coinAnimationStyle = `
  @keyframes coinFall {
    0% {
      transform: translateY(-100px) rotate(0deg) scale(1);
      opacity: 0;
    }
    5% {
      opacity: 1;
    }
    95% {
      opacity: 1;
    }
    100% {
      transform: translateY(calc(100% + 100px)) rotate(360deg) scale(0.8);
      opacity: 0;
    }
  }
  
  .coin-fall {
    animation: coinFall 4s ease-in-out infinite;
    will-change: transform, opacity;
    opacity: 0;
  }
  
  .coin-fall-fast {
    animation-duration: 3s;
  }
  
  .coin-fall-slow {
    animation-duration: 5s;
  }
`;

const useCountdown = (targetDate) => {
  const [timeLeft, setTimeLeft] = useState(null);

  useEffect(() => {
    if (!targetDate) {
      setTimeLeft(null);
      return;
    }
    const target = new Date(targetDate).getTime();
    const tick = () => {
      const diff = target - Date.now();
      if (diff <= 0) {
        setTimeLeft({ expired: true, days: 0, hours: 0, minutes: 0, seconds: 0 });
        return;
      }
      setTimeLeft({
        expired: false,
        days: Math.floor(diff / 86400000),
        hours: Math.floor((diff / 3600000) % 24),
        minutes: Math.floor((diff / 60000) % 60),
        seconds: Math.floor((diff / 1000) % 60)
      });
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [targetDate]);

  return timeLeft;
};

const ORIGINALS_PREVIEW = [
  { to: '/games/blackjack', logo: '/images/originals/blackjack.png', name: 'Blackjack' },
  { to: '/games/mines', logo: '/images/originals/mines.png', name: 'Mines' },
  { to: '/games/limbo', logo: '/images/originals/limbo.png', name: 'Limbo' }
];

const LandingPage = () => {
  const [sections, setSections] = useState([]);
  const [banners, setBanners] = useState([]);
  const [sponsors, setSponsors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [useCustomIcons, setUseCustomIcons] = useState(false);
  const [isUser, setIsUser] = useState(false);
  const [activeSpinEvent, setActiveSpinEvent] = useState(null);
  const countdown = useCountdown(activeSpinEvent?.endDateUtc);

  // Custom icon'ların varlığını kontrol et
  useEffect(() => {
    const checkCustomIcons = async () => {
      try {
        const iconNames = ['sponsors', 'games', 'tickets', 'market'];
        const extensions = ['.png', '.svg', '.jpg', '.jpeg'];
        
        const checks = await Promise.all(
          iconNames.map(async (name) => {
            // Her icon için tüm uzantıları sırayla dene
            for (const ext of extensions) {
              const exists = await new Promise((resolve) => {
                const img = new Image();
                img.onload = () => resolve(true);
                img.onerror = () => resolve(false);
                img.src = `/icons/${name}${ext}`;
              });
              if (exists) return true;
            }
            return false;
          })
        );
        // En az bir custom icon varsa custom icon'ları kullan
        setUseCustomIcons(checks.some(exists => exists));
      } catch (error) {
        setUseCustomIcons(false);
      }
    };
    checkCustomIcons();
  }, []);

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    axios.get('/api/spin-events/active')
      .then((res) => {
        const d = res.data || {};
        setActiveSpinEvent({
          title: d.title,
          prizeAmount: d.prizeAmount || d.prize_amount,
          backgroundImageUrl: d.backgroundImageUrl || d.background_image_url,
          buttonText: d.buttonText || d.button_text,
          buttonLink: d.buttonLink || d.button_link,
          rulesButtonText: d.rulesButtonText || d.rules_button_text,
          rulesButtonLink: d.rulesButtonLink || d.rules_button_link,
          endDateUtc: d.endDateUtc || d.end_date_utc
        });
      })
      .catch(() => setActiveSpinEvent(null));
  }, []);

  // Kullanıcı durumunu kontrol et
  useEffect(() => {
    const checkUser = () => {
      const token = localStorage.getItem('userToken');
      setIsUser(!!token);
    };

    // İlk kontrol
    checkUser();

    // Storage değişikliklerini dinle
    const handleStorageChange = (e) => {
      if (e.key === 'userToken') {
        checkUser();
      }
    };

    // Custom event için listener
    const handleUserTokenChanged = () => {
      checkUser();
    };

    window.addEventListener('storage', handleStorageChange);
    window.addEventListener('userTokenChanged', handleUserTokenChanged);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('userTokenChanged', handleUserTokenChanged);
    };
  }, []);

  const fetchData = async () => {
    try {
      const [sectionsRes, bannersRes, sponsorsRes] = await Promise.all([
        axios.get('/api/homepage'),
        axios.get('/api/banners'),
        axios.get('/api/sponsors').catch(() => ({ data: [] }))
      ]);

      setSections(sectionsRes.data || []);
      setBanners(bannersRes.data || []);
      setSponsors(sponsorsRes.data || []);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const renderSection = (section) => {
    const sectionType = section.type || section.type;
    const title = section.title || '';
    const subtitle = section.subtitle || '';
    const description = section.description || '';
    const imageUrl = section.imageUrl || section.image_url || null;
    
    // Parse images array
    let images = [];
    if (section.images) {
      try {
        const parsed = JSON.parse(section.images);
        if (Array.isArray(parsed) && parsed.length > 0) {
          images = parsed;
        } else if (typeof parsed === 'string') {
          images = [parsed];
        }
      } catch (e) {
        // If not JSON, try as string
        if (typeof section.images === 'string' && section.images.trim() !== '') {
          images = [section.images];
        }
      }
    }
    
    // Fallback to imageUrl if images array is empty
    if (images.length === 0 && imageUrl) {
      images = [imageUrl];
    }
    
    const linkUrl = section.linkUrl || section.link_url || null;
    const buttonText = section.buttonText || section.button_text || '';
    const buttonLink = section.buttonLink || section.button_link || null;
    const extraData = section.extraData || section.extra_data ? JSON.parse(section.extraData || section.extra_data) : {};

    const handleImageClick = () => {
      if (linkUrl && linkUrl !== '' && linkUrl !== '#') {
        if (linkUrl.startsWith('http://') || linkUrl.startsWith('https://')) {
          window.location.href = linkUrl;
        } else {
          window.location.href = linkUrl;
        }
      }
    };

    const renderImageCarousel = (imageList, altText = 'Section') => {
      if (!imageList || imageList.length === 0) return null;
      
      // Filter out empty or invalid URLs
      const validImages = imageList.filter(img => img && img.trim() !== '' && img !== 'null' && img !== 'undefined');
      if (validImages.length === 0) return null;
      
      if (validImages.length === 1) {
        return (
          <div className={`flex justify-center overflow-x-hidden ${linkUrl ? 'cursor-pointer' : ''}`} onClick={linkUrl ? handleImageClick : undefined}>
            <img
              src={validImages[0]}
              alt={altText}
              className={`max-w-full h-auto max-h-[400px] rounded-xl transition-all duration-300 object-contain ${linkUrl ? 'hover:opacity-90' : ''}`}
              style={{ maxWidth: '60%', maxHeight: '400px' }}
              onError={(e) => {
                console.error('Image load error:', validImages[0]);
                e.target.style.display = 'none';
              }}
            />
          </div>
        );
      }

      return (
        <Swiper
          spaceBetween={20}
          slidesPerView={1}
          autoplay={{
            delay: 3000,
            disableOnInteraction: false,
          }}
          pagination={{
            clickable: true,
          }}
          navigation={true}
          modules={[Autoplay, Pagination, Navigation]}
          className="w-full rounded-xl overflow-x-hidden"
        >
          {validImages.map((img, index) => (
            <SwiperSlide key={index}>
              <div className={`flex justify-center overflow-x-hidden ${linkUrl ? 'cursor-pointer' : ''}`} onClick={linkUrl ? handleImageClick : undefined}>
                <img
                  src={img}
                  alt={`${altText} ${index + 1}`}
                  className={`max-w-full h-auto max-h-[400px] rounded-xl transition-all duration-300 object-contain ${linkUrl ? 'hover:opacity-90' : ''}`}
                  style={{ maxWidth: '60%', maxHeight: '400px' }}
                  onError={(e) => {
                    console.error('Image load error:', img);
                    e.target.style.display = 'none';
                  }}
                />
              </div>
            </SwiperSlide>
          ))}
        </Swiper>
      );
    };

    switch (sectionType) {
      case 'hero':
        return (
          <section key={section.id} className="relative min-h-[70vh] flex items-center justify-center overflow-x-hidden">
            <div className="absolute inset-0 bg-gradient-to-br from-navy-900 via-navy-800 to-navy-900">
              <div className="absolute inset-0 opacity-20">
                <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-gold rounded-full blur-3xl animate-pulse"></div>
                <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-yellow-500 rounded-full blur-3xl animate-pulse delay-1000"></div>
              </div>
            </div>
            {images.length > 0 && (
              <div className="absolute inset-0 z-0">
                {images.length === 1 ? (
                  <img
                    src={images[0]}
                    alt={title || 'Hero'}
                    className={`w-full h-full object-cover opacity-30 ${linkUrl ? 'cursor-pointer' : ''}`}
                    onClick={handleImageClick}
                  />
                ) : (
                  <Swiper
                    spaceBetween={0}
                    slidesPerView={1}
                    autoplay={{
                      delay: 4000,
                      disableOnInteraction: false,
                    }}
                    modules={[Autoplay]}
                    className="w-full h-full"
                  >
                    {images.map((img, index) => (
                      <SwiperSlide key={index}>
                        <img
                          src={img}
                          alt={`${title || 'Hero'} ${index + 1}`}
                          className={`w-full h-full object-cover opacity-30 ${linkUrl ? 'cursor-pointer' : ''}`}
                          onClick={handleImageClick}
                        />
                      </SwiperSlide>
                    ))}
                  </Swiper>
                )}
              </div>
            )}
            <div className="relative z-10 text-center px-4 max-w-5xl mx-auto">
              {title && (
                <h1 className="text-5xl md:text-7xl font-bold text-gold mb-6 drop-shadow-[0_0_30px_rgba(212,175,55,0.8)] animate-fade-in">
                  {title}
                </h1>
              )}
              {subtitle && (
                <p className="text-xl md:text-2xl text-white/90 mb-8 font-light animate-fade-in-delay">
                  {subtitle}
                </p>
              )}
              {description && (
                <p className="text-lg text-white/80 mb-8 max-w-3xl mx-auto animate-fade-in-delay">
                  {description}
                </p>
              )}
              {(buttonText && buttonLink) && (
                <div className="flex flex-col sm:flex-row gap-4 justify-center items-center animate-fade-in-delay-2">
                  {buttonLink.startsWith('http') ? (
                    <a
                      href={buttonLink}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="bg-gradient-to-r from-gold to-yellow-500 hover:from-yellow-500 hover:to-gold text-navy-900 font-bold px-8 py-4 rounded-xl transition-all duration-300 shadow-[0_0_20px_rgba(212,175,55,0.5)] hover:shadow-[0_0_30px_rgba(212,175,55,0.8)]"
                    >
                      {buttonText}
                    </a>
                  ) : (
                    <Link
                      to={buttonLink}
                      className="bg-gradient-to-r from-gold to-yellow-500 hover:from-yellow-500 hover:to-gold text-navy-900 font-bold px-8 py-4 rounded-xl transition-all duration-300 shadow-[0_0_20px_rgba(212,175,55,0.5)] hover:shadow-[0_0_30px_rgba(212,175,55,0.8)]"
                    >
                      {buttonText}
                    </Link>
                  )}
                </div>
              )}
            </div>
          </section>
        );

      case 'features':
        const features = extraData.items || [];
        return (
          <section key={section.id} className="py-20 px-4 bg-gradient-to-b from-transparent to-navy-900/30 overflow-x-hidden">
            <div className="max-w-6xl mx-auto overflow-x-hidden">
              {title && (
                <h2 className="text-3xl md:text-4xl font-bold text-center text-gold mb-16 drop-shadow-[0_0_15px_rgba(212,175,55,0.5)]">
                  {title}
                </h2>
              )}
              {subtitle && (
                <p className="text-center text-white/80 mb-12 text-lg">{subtitle}</p>
              )}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-8 overflow-x-hidden">
                {features.map((feature, index) => (
                  <div
                    key={index}
                    className="bg-gradient-card p-8 rounded-xl border border-gold/30 hover:border-gold transition-all duration-300 hover:shadow-[0_0_30px_rgba(212,175,55,0.3)] text-center"
                  >
                    {feature.icon && <div className="text-6xl mb-4">{feature.icon}</div>}
                    {feature.title && <h3 className="text-xl font-bold text-gold mb-3">{feature.title}</h3>}
                    {feature.description && <p className="text-white/80">{feature.description}</p>}
                  </div>
                ))}
              </div>
            </div>
          </section>
        );

      case 'cta':
        return (
          <section key={section.id} className="py-20 px-4 bg-gradient-to-r from-gold/10 via-yellow-500/10 to-gold/10 border-y border-gold/20 overflow-x-hidden">
            <div className="max-w-4xl mx-auto text-center overflow-x-hidden">
              {title && (
                <h2 className="text-4xl md:text-5xl font-bold text-gold mb-6 drop-shadow-[0_0_20px_rgba(212,175,55,0.6)]">
                  {title}
                </h2>
              )}
              {subtitle && (
                <p className="text-xl text-white/90 mb-8">{subtitle}</p>
              )}
              {description && (
                <p className="text-lg text-white/80 mb-8">{description}</p>
              )}
              {(buttonText && buttonLink) && (
                buttonLink.startsWith('http') ? (
                  <a
                    href={buttonLink}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-block bg-gradient-to-r from-gold to-yellow-500 hover:from-yellow-500 hover:to-gold text-navy-900 font-bold px-10 py-4 rounded-xl transition-all duration-300 shadow-[0_0_25px_rgba(212,175,55,0.6)] hover:shadow-[0_0_35px_rgba(212,175,55,0.9)]"
                  >
                    {buttonText}
                  </a>
                ) : (
                  <Link
                    to={buttonLink}
                    className="inline-block bg-gradient-to-r from-gold to-yellow-500 hover:from-yellow-500 hover:to-gold text-navy-900 font-bold px-10 py-4 rounded-xl transition-all duration-300 shadow-[0_0_25px_rgba(212,175,55,0.6)] hover:shadow-[0_0_35px_rgba(212,175,55,0.9)]"
                  >
                    {buttonText}
                  </Link>
                )
              )}
            </div>
          </section>
        );

      case 'image-text':
        return (
          <section key={section.id} className="py-16 px-4 overflow-x-hidden">
            <div className="max-w-6xl mx-auto overflow-x-hidden">
              <div className={`grid grid-cols-1 md:grid-cols-2 gap-12 items-center ${extraData.imagePosition === 'right' ? 'md:flex-row-reverse' : ''}`}>
                {images.length > 0 && (
                  <div className="order-2 md:order-1">
                    {renderImageCarousel(images, title || 'Section')}
                  </div>
                )}
                <div className="order-1 md:order-2">
                  {title && (
                    <h2 className="text-3xl md:text-4xl font-bold text-gold mb-6 drop-shadow-[0_0_15px_rgba(212,175,55,0.5)]">
                      {title}
                    </h2>
                  )}
                  {subtitle && (
                    <p className="text-xl text-white/90 mb-4">{subtitle}</p>
                  )}
                  {description && (
                    <p className="text-lg text-white/80 mb-6">{description}</p>
                  )}
                  {(buttonText && buttonLink) && (
                    buttonLink.startsWith('http') ? (
                      <a
                        href={buttonLink}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-block bg-gradient-to-r from-gold to-yellow-500 hover:from-yellow-500 hover:to-gold text-navy-900 font-bold px-8 py-3 rounded-xl transition-all duration-300"
                      >
                        {buttonText}
                      </a>
                    ) : (
                      <Link
                        to={buttonLink}
                        className="inline-block bg-gradient-to-r from-gold to-yellow-500 hover:from-yellow-500 hover:to-gold text-navy-900 font-bold px-8 py-3 rounded-xl transition-all duration-300"
                      >
                        {buttonText}
                      </Link>
                    )
                  )}
                </div>
              </div>
            </div>
          </section>
        );

      case 'content':
        return (
          <section key={section.id} className="py-16 px-4 bg-gradient-to-b from-transparent to-navy-900/20 overflow-x-hidden">
            <div className="max-w-4xl mx-auto overflow-x-hidden">
              {title && (
                <h2 className="text-3xl md:text-4xl font-bold text-center text-gold mb-8 drop-shadow-[0_0_15px_rgba(212,175,55,0.5)]">
                  {title}
                </h2>
              )}
              {subtitle && (
                <p className="text-xl text-center text-white/90 mb-6">{subtitle}</p>
              )}
              {description && (
                <div className="text-lg text-white/80 prose prose-invert max-w-none mb-8" dangerouslySetInnerHTML={{ __html: description.replace(/\n/g, '<br />') }} />
              )}
              {images.length > 0 ? (
                <div className="mt-8">
                  {renderImageCarousel(images, title || 'Content')}
                </div>
              ) : imageUrl ? (
                <div className="mt-8">
                  {renderImageCarousel([imageUrl], title || 'Content')}
                </div>
              ) : null}
            </div>
          </section>
        );

      case 'activity-cards':
        const activityCards = extraData.cards || [];
        return (
          <section key={section.id} className="py-16 px-4 overflow-x-hidden">
            <div className="max-w-7xl mx-auto overflow-x-hidden">
              {title && (
                <h2 className="text-3xl md:text-4xl font-bold text-center text-gold mb-12 drop-shadow-[0_0_15px_rgba(212,175,55,0.5)]">
                  {title}
                </h2>
              )}
              {subtitle && (
                <p className="text-xl text-center text-white/90 mb-8">{subtitle}</p>
              )}
              <div className="grid grid-cols-2 md:grid-cols-2 lg:grid-cols-4 gap-6">
                {activityCards.map((card, index) => {
                  const gradientFrom = card.gradientFrom || 'from-purple-500';
                  const gradientTo = card.gradientTo || 'to-navy-600';
                  const iconImage = card.iconImage || '';
                  const cardTitle = card.title || '';
                  const cardDescription = card.description || '';
                  const buttonText = card.buttonText || '';
                  const buttonLink = card.buttonLink || '#';
                  
                  return (
                    <div
                      key={index}
                      className="bg-gradient-card rounded-xl p-6 border border-gold/30 hover:border-gold transition-all duration-300 hover:shadow-[0_0_30px_rgba(212,175,55,0.3)] flex flex-col"
                    >
                      {/* Icon Container */}
                      <div className="w-16 h-16 rounded-lg flex items-center justify-center mb-4 flex-shrink-0">
                        {iconImage ? (
                          <img
                            src={iconImage}
                            alt={cardTitle}
                            className="w-12 h-12 object-contain"
                            onError={(e) => {
                              e.target.style.display = 'none';
                            }}
                          />
                        ) : (
                          <div className="text-white text-2xl">📦</div>
                        )}
                      </div>

                      {/* Content */}
                      <div className="flex-1">
                        {cardTitle && (
                          <h3 className="text-lg font-bold text-white mb-2">{cardTitle}</h3>
                        )}
                        {cardDescription && (
                          <p className="text-white/70 text-sm mb-4 flex-1">{cardDescription}</p>
                        )}
                      </div>

                      {/* Button */}
                      {buttonText && (
                        <button
                          onClick={() => {
                            if (buttonLink && buttonLink !== '#') {
                              if (buttonLink.startsWith('http')) {
                                window.open(buttonLink, '_blank', 'noopener,noreferrer');
                              } else {
                                window.location.href = buttonLink;
                              }
                            }
                          }}
                          className={`w-full py-2.5 px-4 rounded-lg font-semibold text-white transition-all duration-300 bg-gradient-to-r ${gradientFrom} ${gradientTo} hover:opacity-90 hover:shadow-lg`}
                        >
                          {buttonText}
                        </button>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          </section>
        );

      default:
        return (
          <section key={section.id} className="py-16 px-4 overflow-x-hidden">
            <div className="max-w-6xl mx-auto overflow-x-hidden">
              {title && <h2 className="text-3xl font-bold text-gold mb-6">{title}</h2>}
              {subtitle && <p className="text-xl text-white/90 mb-4">{subtitle}</p>}
              {description && <p className="text-lg text-white/80 mb-6">{description}</p>}
              {images.length > 0 ? (
                <div className="mb-6">
                  {renderImageCarousel(images, title || 'Section')}
                </div>
              ) : imageUrl ? (
                <div className="mb-6">
                  {renderImageCarousel([imageUrl], title || 'Section')}
                </div>
              ) : null}
            </div>
          </section>
        );
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-16 w-16 border-t-2 border-b-2 border-gold"></div>
      </div>
    );
  }

  // Button component for reusability
  const ActionButton = ({ to, icon, text, customIconName }) => (
    <Link
      to={to}
      className="group relative w-full bg-gradient-to-r from-navy-900 via-navy-800 to-navy-900 hover:from-navy-800 hover:via-navy-700 hover:to-navy-800 text-gold font-bold px-4 md:px-6 py-3 md:py-4 rounded-xl transition-all duration-300 shadow-[0_0_20px_rgba(212,175,55,0.3)] hover:shadow-[0_0_30px_rgba(212,175,55,0.6)] border-2 border-gold/30 hover:border-gold text-center text-sm md:text-base flex items-center justify-center gap-2"
    >
      <span className="relative z-10 flex items-center justify-center gap-2">
        {customIconName && useCustomIcons ? (
          <CustomIcon name={customIconName} className="w-6 h-6 md:w-8 md:h-8" />
        ) : (
          icon && React.createElement(icon, { className: "w-6 h-6 md:w-8 md:h-8 text-gold" })
        )}
        <span className="text-gold drop-shadow-[0_0_8px_rgba(212,175,55,0.8)]">{text}</span>
      </span>
      <div className="absolute inset-0 rounded-xl bg-gradient-to-r from-gold/10 via-yellow-500/10 to-gold/10 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
    </Link>
  );

  return (
    <div className="w-full overflow-x-hidden">
      {/* Coin animasyon CSS'i */}
      <style>{coinAnimationStyle}</style>
      
      {/* Sponsor Logo Marquee - Kayan Yazı */}
      <SponsorLogoMarquee sponsors={sponsors} />

      {/* Quick Action Buttons Section */}
      <section className="py-8 md:py-12 px-4 bg-gradient-to-b from-navy-900/50 via-transparent to-transparent">
        <div className="max-w-6xl mx-auto overflow-x-hidden">
          {/* Çevrim Etkinliği Hero - Ana Sayfanın En Öne Çıkan Alanı */}
          <div className="w-full flex justify-center mb-6 md:mb-8">
            <div className="group relative w-full max-w-4xl rounded-2xl border-2 border-gold/40 hover:border-gold/70 shadow-[0_0_35px_rgba(212,175,55,0.3)] hover:shadow-[0_0_50px_rgba(212,175,55,0.5)] transition-all duration-300 overflow-hidden">
              {/* Arkaplan */}
              <div className="absolute inset-0">
                {activeSpinEvent?.backgroundImageUrl && (
                  <img
                    src={activeSpinEvent.backgroundImageUrl}
                    alt=""
                    className="w-full h-full object-cover opacity-35 group-hover:opacity-45 transition-opacity duration-300"
                  />
                )}
                <div className="absolute inset-0 bg-gradient-to-br from-blue-950 via-blue-900/90 to-navy-950"></div>
                <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-transparent"></div>
                <div className="absolute top-1/4 left-1/4 w-64 h-64 bg-gold/20 rounded-full blur-3xl"></div>
              </div>

              {/* Coin düşme animasyonu */}
              <div className="absolute inset-0 pointer-events-none overflow-hidden">
                {[...Array(15)].map((_, i) => {
                  const leftPosition = 5 + (i * 6.5) % 90;
                  const delay = i * 0.4;
                  const speedClass = i % 3 === 0 ? 'coin-fall-fast' : i % 3 === 1 ? 'coin-fall-slow' : '';
                  const coinSize = 14 + (i % 3) * 4;

                  return (
                    <div
                      key={`coin-${i}`}
                      className={`absolute coin-fall ${speedClass}`}
                      style={{
                        left: `${leftPosition}%`,
                        width: `${coinSize}px`,
                        height: `${coinSize}px`,
                        animationDelay: `${delay}s`,
                      }}
                    >
                      <svg
                        width={coinSize}
                        height={coinSize}
                        viewBox="0 0 24 24"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                        style={{
                          filter: 'drop-shadow(0 0 6px rgba(255, 215, 0, 1)) drop-shadow(0 0 12px rgba(255, 215, 0, 0.6))',
                        }}
                      >
                        <circle cx="12" cy="12" r="10" fill="#FFD700" stroke="#FFA500" strokeWidth="1.5"/>
                        <circle cx="12" cy="12" r="7" fill="#FFED4E" opacity="0.6"/>
                        <path d="M12 5 L12 19 M5 12 L19 12" stroke="#FFA500" strokeWidth="1" opacity="0.5"/>
                        <circle cx="12" cy="12" r="2" fill="#FFA500"/>
                      </svg>
                    </div>
                  );
                })}
              </div>

              <div className="relative z-10 px-6 md:px-14 py-9 md:py-14 text-center">
                <span className="inline-flex items-center gap-2 rounded-full bg-gold/15 border border-gold/40 px-4 py-1.5 text-[11px] md:text-sm font-bold uppercase tracking-widest text-gold mb-4">
                  <CustomIcon name="cevrim" className="w-4 h-4 md:w-5 md:h-5" />
                  Çevrim Etkinliği
                </span>
                <h2 className="text-2xl md:text-4xl lg:text-5xl font-extrabold text-white drop-shadow-[0_2px_10px_rgba(0,0,0,0.6)] mb-3 leading-tight">
                  {activeSpinEvent?.title || 'Büyük Ödüllü Çevrim Etkinliği'}
                </h2>
                {activeSpinEvent?.prizeAmount && (
                  <div className="text-3xl md:text-5xl font-extrabold text-gold drop-shadow-[0_0_20px_rgba(212,175,55,0.7)] mb-6">
                    {activeSpinEvent.prizeAmount}
                  </div>
                )}

                {countdown && !countdown.expired && (
                  <div className="flex justify-center gap-2 md:gap-3 mb-7">
                    {[
                      ['Gün', countdown.days],
                      ['Saat', countdown.hours],
                      ['Dakika', countdown.minutes],
                      ['Saniye', countdown.seconds]
                    ].map(([label, value]) => (
                      <div key={label} className="bg-black/40 backdrop-blur-sm border border-gold/30 rounded-lg px-3 py-2 min-w-[54px] md:min-w-[72px]">
                        <div className="text-lg md:text-2xl font-bold text-white tabular-nums">{String(value).padStart(2, '0')}</div>
                        <div className="text-[9px] md:text-[10px] uppercase tracking-wide text-white/60">{label}</div>
                      </div>
                    ))}
                  </div>
                )}

                <div className="flex flex-col sm:flex-row gap-3 justify-center flex-wrap">
                  <Link
                    to={activeSpinEvent?.buttonLink || '/spin-event'}
                    className="inline-flex items-center justify-center bg-gradient-to-r from-gold to-yellow-500 hover:from-yellow-500 hover:to-gold text-navy-900 font-bold px-8 py-3.5 rounded-xl transition-all duration-300 shadow-[0_0_20px_rgba(212,175,55,0.5)] hover:shadow-[0_0_30px_rgba(212,175,55,0.8)]"
                  >
                    {activeSpinEvent?.buttonText || 'Hemen Katıl'}
                  </Link>
                  <Link
                    to="/spin-event"
                    className="inline-flex items-center justify-center bg-white/10 hover:bg-white/15 text-white font-semibold px-8 py-3.5 rounded-xl border border-white/20 transition-all duration-300"
                  >
                    Sıralamayı Gör
                  </Link>
                  {activeSpinEvent?.rulesButtonLink && (
                    activeSpinEvent.rulesButtonLink.startsWith('http') ? (
                      <a
                        href={activeSpinEvent.rulesButtonLink}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center justify-center bg-white/10 hover:bg-white/15 text-white font-semibold px-8 py-3.5 rounded-xl border border-white/20 transition-all duration-300"
                      >
                        {activeSpinEvent.rulesButtonText || 'Kurallar ve Detaylar'}
                      </a>
                    ) : (
                      <Link
                        to={activeSpinEvent.rulesButtonLink}
                        className="inline-flex items-center justify-center bg-white/10 hover:bg-white/15 text-white font-semibold px-8 py-3.5 rounded-xl border border-white/20 transition-all duration-300"
                      >
                        {activeSpinEvent.rulesButtonText || 'Kurallar ve Detaylar'}
                      </Link>
                    )
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Moon Butonu - Çevrim Etkinliği'nin altında */}
          <div className="w-full flex justify-center mb-6 md:mb-8">
            <Link
              to="/moon"
              className="shimmer-button group relative w-full max-w-2xl text-white font-bold px-8 md:px-12 py-5 md:py-6 rounded-xl transition-all duration-300 border-2 text-center text-lg md:text-xl flex items-center justify-center gap-3 overflow-hidden"
              style={{
                background: 'linear-gradient(90deg, #01121C 0%, #0a2536 50%, #01121C 100%)',
                borderColor: 'rgba(26,212,255,0.4)',
                boxShadow: '0 0 30px rgba(26,212,255,0.35)',
              }}
            >
              <span className="relative z-10 flex items-center gap-3">
                <MoonLogo size="sm" />
                <span className="text-white font-bold drop-shadow-[0_0_4px_rgba(0,0,0,0.5)]">
                  1000x Kaldıraçla Moon'da Kazan
                </span>
                <span
                  className="hidden sm:inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide"
                  style={{ background: 'linear-gradient(135deg,#1AD4FF,#009ACA)', color: '#011825' }}
                >
                  Yeni
                </span>
              </span>
              <div
                className="absolute inset-0 rounded-xl opacity-0 group-hover:opacity-100 transition-opacity duration-300"
                style={{ background: 'linear-gradient(90deg, rgba(26,212,255,0.12), rgba(0,154,202,0.12), rgba(26,212,255,0.12))' }}
              ></div>
            </Link>
          </div>

          {/* Saguex Originals Önizleme */}
          <div className="mb-6 md:mb-10">
            <div className="flex items-center justify-between mb-3 px-1">
              <h3 className="text-lg md:text-2xl font-bold text-gold drop-shadow-[0_0_10px_rgba(212,175,55,0.4)]">Saguex Originals</h3>
              <Link to="/games" className="text-xs md:text-sm text-white/60 hover:text-gold transition-colors">
                Tümünü gör ›
              </Link>
            </div>
            <div className="grid grid-cols-3 gap-2 md:gap-5">
              {ORIGINALS_PREVIEW.map((g) => (
                <Link
                  key={g.to}
                  to={g.to}
                  className="group/card bg-gradient-card border border-gold/20 hover:border-gold rounded-xl p-3 md:p-6 text-center transition-all duration-300 hover:-translate-y-1 hover:shadow-[0_0_20px_rgba(212,175,55,0.3)]"
                >
                  <div className="mx-auto mb-1.5 md:mb-3 h-10 w-10 md:h-16 md:w-16 overflow-hidden rounded-lg border border-gold/20 transition-transform duration-300 group-hover/card:scale-110">
                    <img src={g.logo} alt={g.name} className="h-full w-full object-cover" />
                  </div>
                  <div className="text-xs md:text-lg font-semibold text-white group-hover/card:text-gold transition-colors">{g.name}</div>
                </Link>
              ))}
            </div>
          </div>

          {/* Desktop: 2'li 2'li + En Alta Tek */}
          <div className="hidden md:block">
            <div className="grid grid-cols-2 gap-3 md:gap-4 max-w-2xl mx-auto mb-3">
              <ActionButton to="/sponsors" icon={FaUsers} text="Sponsorlar" customIconName="sponsors" />
              <ActionButton to="/games" icon={FaGamepad} text="Oyunlar" customIconName="games" />
              <ActionButton to="/tickets" icon={FaTicketAlt} text="Bilet Al" customIconName="tickets" />
              <ActionButton to="/promo-code" icon={FaGift} text="Puan Kodu" />
            </div>
            {/* En Alta Tek Buton - Ortalı */}
            <div className="flex justify-center max-w-2xl mx-auto">
              <ActionButton to="/market" icon={FaShoppingCart} text="Market" customIconName="market" />
            </div>
          </div>

          {/* Mobile: 2x2 Grid + Market Butonu Altında */}
          <div className="md:hidden">
            <div className="grid grid-cols-2 gap-3 mb-3">
              <ActionButton to="/sponsors" icon={FaUsers} text="Sponsorlar" customIconName="sponsors" />
              <ActionButton to="/games" icon={FaGamepad} text="Oyunlar" customIconName="games" />
              <ActionButton to="/promo-code" icon={FaGift} text="Puan Kodu" />
              <ActionButton to="/tickets" icon={FaTicketAlt} text="Bilet Al" customIconName="tickets" />
            </div>
            {/* Mobilde Market Butonu Altında Ortalı */}
            <div className="flex justify-center">
              <ActionButton to="/market" icon={FaShoppingCart} text="Market" customIconName="market" />
            </div>
          </div>

          {/* Giriş Yapmamış Kullanıcılar için Butonlar */}
          {!isUser && (
            <div className="flex flex-col sm:flex-row gap-4 md:gap-6 justify-center items-center mt-6">
              <Link
                to="/login"
                className="group relative w-full sm:w-auto min-w-[200px] md:min-w-[220px] bg-gradient-to-r from-navy-900 via-navy-800 to-navy-900 hover:from-navy-800 hover:via-navy-700 hover:to-navy-800 text-gold font-bold px-6 md:px-8 py-3 md:py-4 rounded-xl transition-all duration-300 shadow-[0_0_20px_rgba(212,175,55,0.3)] hover:shadow-[0_0_30px_rgba(212,175,55,0.6)] border-2 border-gold/30 hover:border-gold text-center text-base md:text-lg"
              >
                <span className="relative z-10 text-gold drop-shadow-[0_0_8px_rgba(212,175,55,0.8)]">
                  Giriş Yap
                </span>
                <div className="absolute inset-0 rounded-xl bg-gradient-to-r from-gold/10 via-yellow-500/10 to-gold/10 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
              </Link>

              <Link
                to="/register"
                className="group relative w-full sm:w-auto min-w-[200px] md:min-w-[220px] bg-gradient-to-r from-gold to-yellow-500 hover:from-yellow-500 hover:to-gold text-navy-900 font-bold px-6 md:px-8 py-3 md:py-4 rounded-xl transition-all duration-300 shadow-[0_0_20px_rgba(212,175,55,0.5)] hover:shadow-[0_0_30px_rgba(212,175,55,0.8)] text-center text-base md:text-lg"
              >
                <span className="relative z-10">
                  Kayıt Ol
                </span>
                <div className="absolute inset-0 rounded-xl bg-gradient-to-r from-yellow-500 to-gold opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
              </Link>
            </div>
          )}
        </div>
      </section>

      {/* Render Dynamic Sections */}
      {sections.map(section => renderSection(section))}

      {/* Banner Section */}
      {banners.length > 0 && (
        <section className="py-8 px-4 overflow-x-hidden">
          <div className="max-w-7xl mx-auto overflow-x-hidden">
            <div className="space-y-4">
              {banners.map((banner) => (
                <div key={banner.id} className="flex justify-center">
                  {banner.link ? (
                    <a
                      href={banner.link}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="block hover:opacity-90 transition-opacity"
                    >
                      <img
                        src={banner.imageUrl || banner.image_url}
                        alt={banner.title || 'Banner'}
                        className="max-w-full h-auto max-h-[400px] rounded-lg shadow-lg object-contain"
                        style={{ maxWidth: '60%', maxHeight: '400px' }}
                      />
                    </a>
                  ) : (
                    <img
                      src={banner.imageUrl || banner.image_url}
                      alt={banner.title || 'Banner'}
                      className="max-w-full h-auto max-h-[400px] rounded-lg shadow-lg object-contain"
                      style={{ maxWidth: '60%', maxHeight: '400px' }}
                    />
                  )}
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Hero Carousel */}
      <section className="py-16 px-4 overflow-x-hidden">
        <div className="max-w-6xl mx-auto overflow-x-hidden">
          <HeroCarousel />
        </div>
      </section>
    </div>
  );
};

export default LandingPage;
